import { useState, useEffect } from "react"
import { UserPlus, Upload, Download, AlertCircle, Loader2 } from "lucide-react"
import { Button } from "../components/ui/button"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import { StudentStats } from "../components/ui/school/data-center/student-stats"
import { StudentTable } from "../components/ui/school/data-center/student-table"
import { AddStudentModal } from "../components/ui/school/data-center/add-student-modal"
import { EditStudentModal } from "../components/ui/school/data-center/edit-student-modal"
import { DeleteConfirmModal } from "../components/ui/school/data-center/delete-confirm-modal"
import { DownloadFormatModal } from "../components/ui/school/data-center/download-format-modal"
import { UploadStudentsDataModal } from "../components/ui/school/data-center/upload-students-data-modal"
import { listStudents, updateStudent, deleteStudent, bulkImportStudents, exportStudentsCSV } from "../hooks/api/schoolApi"

const DataCenterPage = () => {
  const [students, setStudents] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isDownloadFormatModalOpen, setIsDownloadFormatModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [deleteStudentId, setDeleteStudentId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const studentsPerPage = 10

  const mapStudent = (s) => ({
    id: s._id,
    name: `${s.firstName} ${s.lastName}`,
    studentId: s.studentId,
    class: s.classLevel,
    gpa: String(s.performanceMetrics?.academicScore ?? "0"),
    careerPath: s.careerInterests?.[0] ?? "—",
    guardianName: "—",
    guardianPhone: "—",
    guardianEmail: "—",
    initials: (`${s.firstName?.[0] ?? ""}${s.lastName?.[0] ?? ""}`).toUpperCase(),
    initialsColor: "teal",
    status: s.status,
  })

  const load = (page = currentPage) => {
    setLoading(true)
    setError(null)
    listStudents({ page, limit: studentsPerPage })
      .then((res) => {
        const items = res.students ?? res ?? []
        setStudents(items.map(mapStudent))
        setTotal(res.total ?? items.length)
      })
      .catch(err => setError(err?.response?.data?.error?.message ?? "Could not load students."))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [currentPage])

  const totalPages = Math.ceil(total / studentsPerPage)
  const avgPerformance = students.length
    ? (students.reduce((sum, s) => sum + Number(s.gpa ?? 0), 0) / students.length).toFixed(1)
    : "0"
  const careerCounts = students.reduce((acc, s) => { acc[s.careerPath] = (acc[s.careerPath] || 0) + 1; return acc }, {})
  const topCareer = Object.entries(careerCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "N/A"

  const handleAddStudent = async (newStudent) => {
    await bulkImportStudents([{
      firstName: newStudent.name?.split(" ")[0] ?? newStudent.name,
      lastName: newStudent.name?.split(" ").slice(1).join(" ") ?? "-",
      email: newStudent.email ?? `student${Date.now()}@school.com`,
      classLevel: newStudent.class ?? newStudent.classLevel ?? "",
      admissionNumber: newStudent.studentId,
    }]).catch(() => {})
    load(1)
  }

  const handleEditStudent = (student) => {
    setSelectedStudent(student)
    setIsEditModalOpen(true)
  }

  const handleUpdateStudent = async (updatedStudent) => {
    await updateStudent(updatedStudent.id, {
      firstName: updatedStudent.name?.split(" ")[0],
      lastName: updatedStudent.name?.split(" ").slice(1).join(" "),
      classLevel: updatedStudent.class,
    }).catch(() => {})
    setStudents((prev) => prev.map((s) => (s.id === updatedStudent.id ? { ...s, ...updatedStudent } : s)))
  }

  const handleDeleteClick = (id) => {
    setDeleteStudentId(id)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    await deleteStudent(deleteStudentId).catch(() => {})
    setStudents((prev) => prev.filter((s) => s.id !== deleteStudentId))
    setTotal((t) => t - 1)
    setIsDeleteModalOpen(false)
    setDeleteStudentId("")
  }

  const handleUploadFile = async (file) => {
    // Minimal CSV parse — expects header row: firstName,lastName,email,classLevel
    const text = await file.text().catch(() => "")
    const lines = text.split("\n").filter(Boolean)
    const rows = lines.slice(1).map((line) => {
      const [firstName, lastName, email, classLevel, admissionNumber] = line.split(",").map((v) => v.trim())
      return { firstName, lastName, email, classLevel, admissionNumber }
    }).filter((r) => r.email)
    if (rows.length) {
      await bulkImportStudents(rows).catch(() => {})
      load(1)
    }
  }

  const handleExport = async () => {
    const blob = await exportStudentsCSV().catch(() => new Blob([""], { type: "text/csv" }))
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "students.csv"
    a.click()
    URL.revokeObjectURL(url)
  }

  const studentToDelete = students.find((s) => s.id === deleteStudentId)

  return (
    <SchoolLayout>
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-3xl font-bold text-secondary mb-2">Data Center</h1>
            <p className="text-muted-foreground">
              Manage student records, academic performance, and guardian details.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => setIsDownloadFormatModalOpen(true)}
              variant="outline"
              className="border-teal-600 text-teal-600 hover:bg-teal-50"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Format
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              className="border-green-600 text-green-600 hover:bg-green-50"
            >
              <Download className="w-5 h-5 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={() => setIsUploadModalOpen(true)}
              variant="outline"
              className="border-primary text-primary hover:bg-primary/10"
            >
              <Upload className="w-5 h-5 mr-2" />
              Upload Students Data
            </Button>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Add Student
            </Button>
          </div>
        </div>

        <StudentStats totalStudents={total} avgPerformance={avgPerformance} topCareer={topCareer} />

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading students…</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700 max-w-lg mt-4">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        ) : (
          <StudentTable
            students={students}
            onEdit={handleEditStudent}
            onDelete={handleDeleteClick}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <AddStudentModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={handleAddStudent} />

      <UploadStudentsDataModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadFile}
      />

      <DownloadFormatModal isOpen={isDownloadFormatModalOpen} onClose={() => setIsDownloadFormatModalOpen(false)} />

      <EditStudentModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedStudent(null) }}
        onUpdate={handleUpdateStudent}
        student={selectedStudent}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteStudentId("") }}
        onConfirm={handleConfirmDelete}
        studentName={studentToDelete?.name || ""}
      />
    </SchoolLayout>
  )
}

export default DataCenterPage
